import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';

import { localeFor, useI18n } from '../i18n';
import { SIGNS } from '../tarot/astro/zodiac';
import { PageShell } from '../tarot/components/PageShell';
import { DECK, SUITS } from '../tarot/deck';
import { interpretCard } from '../tarot/interpret';

import { CallToAction } from './CallToAction';
import { ChironCalculator } from './ChironCalculator';
import { isSign, periodsIn } from './chironSigns';
import { fill, LANDING_ORDER, landingCopy, seoUi } from './copy';
import { Seo } from './Seo';
import {
    CARD_PATH,
    cardBySlug,
    cardPath,
    cardSlug,
    chironSignPath,
    LandingId,
} from './slugs';

const Article: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Paper
        component="article"
        sx={{ p: { xs: 2.5, sm: 4 }, background: 'rgba(21,14,36,.88)' }}
    >
        {children}
    </Paper>
);

const LinkList: React.FC<{
    title: string;
    items: { to: string; label: string }[];
}> = ({ title, items }) => (
    <Box component="nav" aria-label={title} sx={{ mt: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
            {title}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
            {items.map((i) => (
                <Link key={i.to} component={RouterLink} to={i.to}>
                    {i.label}
                </Link>
            ))}
        </Stack>
    </Box>
);

/** A content landing page (free birth chart, Chiron calculator...) with its questions and a call to action. */
export const LandingPage: React.FC<{ id: LandingId }> = ({ id }) => {
    const { language } = useI18n();
    const copy = landingCopy(language, id);
    const ui = seoUi(language);
    return (
        <PageShell to="/">
            <Seo
                title={copy.title}
                description={copy.description}
                path={`/${id}`}
            />
            <Article>
                <Typography variant="h4" component="h1" gutterBottom>
                    {copy.h1}
                </Typography>
                <Typography paragraph>{copy.intro}</Typography>
                {copy.calculator && <ChironCalculator />}
                {copy.sections.map((s) => (
                    <React.Fragment key={s.h}>
                        <Typography variant="h5" component="h2" sx={{ mt: 3 }}>
                            {s.h}
                        </Typography>
                        <Typography paragraph>{s.p}</Typography>
                    </React.Fragment>
                ))}
                {copy.faq.map((f) => (
                    <React.Fragment key={f.q}>
                        <Typography variant="h6" component="h3" sx={{ mt: 2 }}>
                            {f.q}
                        </Typography>
                        <Typography paragraph>{f.a}</Typography>
                    </React.Fragment>
                ))}
                <CallToAction {...copy.cta} />
            </Article>
            <LinkList
                title={ui.explore}
                items={[
                    ...LANDING_ORDER.filter((l) => l !== id).map((l) => ({
                        to: `/${l}`,
                        label: landingCopy(language, l).h1,
                    })),
                    {
                        to: CARD_PATH,
                        label: ui.card.indexTitle.split(' | ')[0],
                    },
                ]}
            />
        </PageShell>
    );
};

const monthYear = (d: Date, language: string) =>
    d.toLocaleDateString(language, {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    });

/** One page per sign: Chiron in Aries, Chiron in Taurus... */
export const ChironSignPage: React.FC = () => {
    const { sign } = useParams();
    const { language } = useI18n();
    if (!isSign(sign)) return <Navigate to="/chiron-in-astrology" replace />;
    const ui = seoUi(language).sign;
    const astro = localeFor(language).astro;
    const name = astro.signs[sign].name;
    const vars = { sign: name };
    const detail = astro.chironDetail[sign];
    const periods = periodsIn(sign);
    return (
        <PageShell to="/chiron-in-astrology">
            <Seo
                title={fill(ui.title, vars)}
                description={fill(ui.description, vars)}
                path={chironSignPath(sign)}
            />
            <Article>
                <Typography variant="h4" component="h1" gutterBottom>
                    {fill(ui.h1, vars)}
                </Typography>
                <Typography paragraph>{astro.inSign.chiron?.[sign]}</Typography>
                <Typography variant="h5" component="h2" sx={{ mt: 3 }}>
                    {ui.woundTitle}
                </Typography>
                <Typography paragraph>{detail.wound}</Typography>
                <Typography variant="h5" component="h2" sx={{ mt: 3 }}>
                    {ui.giftTitle}
                </Typography>
                <Typography paragraph>{detail.gift}</Typography>
                <Typography variant="h5" component="h2" sx={{ mt: 3 }}>
                    {fill(ui.whenTitle, vars)}
                </Typography>
                <Typography paragraph>
                    {periods.length
                        ? fill(ui.when, vars)
                        : fill(ui.whenNone, vars)}
                </Typography>
                <Box component="ul" sx={{ mt: 0 }}>
                    {periods.map((p) => (
                        <li key={p.from.getTime()}>
                            {monthYear(p.from, language)} {ui.present}{' '}
                            {monthYear(p.to, language)}
                        </li>
                    ))}
                </Box>
                <CallToAction
                    title={fill(ui.cta.title, vars)}
                    body={ui.cta.body}
                    button={ui.cta.button}
                    to="/birth-chart"
                />
            </Article>
            <LinkList
                title={ui.allSigns}
                items={SIGNS.filter((s) => s !== sign).map((s) => ({
                    to: chironSignPath(s),
                    label: astro.signs[s].name,
                }))}
            />
        </PageShell>
    );
};

/** One page per tarot card, built from the card's own reading, with a call to action to the card of the day. */
export const CardPage: React.FC = () => {
    const { slug } = useParams();
    const { language } = useI18n();
    const card = cardBySlug(slug);
    if (!card) return <Navigate to={CARD_PATH} replace />;
    const ui = seoUi(language).card;
    const up = interpretCard(card.id, false, 'general', language);
    const rev = interpretCard(card.id, true, 'general', language);
    const vars = { card: up.name };
    const nearby = DECK.filter(
        (c) => c.arcana === card.arcana && c.suit === card.suit && c !== card
    ).slice(0, 6);
    return (
        <PageShell to={CARD_PATH}>
            <Seo
                title={fill(ui.title, vars)}
                description={fill(ui.description, vars)}
                path={cardPath(card)}
            />
            <Article>
                <Typography variant="h4" component="h1" gutterBottom>
                    {up.name}
                </Typography>
                <Typography color="text.secondary" paragraph>
                    {ui.keywords}: {up.keywords}
                </Typography>
                <Typography variant="h5" component="h2" sx={{ mt: 2 }}>
                    {ui.upright}
                </Typography>
                <Typography paragraph>{up.meaning}</Typography>
                <Typography variant="h5" component="h2" sx={{ mt: 2 }}>
                    {ui.reversed}
                </Typography>
                <Typography paragraph>{rev.meaning}</Typography>
                <Typography variant="h5" component="h2" sx={{ mt: 2 }}>
                    {ui.advice}
                </Typography>
                <Typography paragraph>{up.advice}</Typography>
                <CallToAction {...ui.cta} to="/ritual/daily" />
            </Article>
            <LinkList
                title={ui.more}
                items={[
                    { to: CARD_PATH, label: ui.indexTitle.split(' | ')[0] },
                    ...nearby.map((c) => ({
                        to: cardPath(c),
                        label: interpretCard(c.id, false, 'general', language)
                            .name,
                    })),
                ]}
            />
        </PageShell>
    );
};

/** The list of all 78 cards, each a link to its page. */
export const CardIndexPage: React.FC = () => {
    const { language } = useI18n();
    const ui = seoUi(language).card;
    const nameOf = (id: string) =>
        interpretCard(id, false, 'general', language).name;
    const groups = [
        {
            key: 'major',
            label: ui.major,
            cards: DECK.filter((c) => c.arcana === 'major'),
        },
        ...SUITS.map((s) => ({
            key: s,
            label: ui.suits[s],
            cards: DECK.filter((c) => c.suit === s),
        })),
    ];
    return (
        <PageShell to="/">
            <Seo
                title={ui.indexTitle}
                description={ui.indexDescription}
                path={CARD_PATH}
            />
            <Article>
                <Typography variant="h4" component="h1" gutterBottom>
                    {ui.indexTitle.split(' | ')[0]}
                </Typography>
                <Typography paragraph>{ui.indexIntro}</Typography>
                {groups.map((g) => (
                    <Box key={g.key} component="section">
                        <Typography variant="h5" component="h2" sx={{ mt: 3 }}>
                            {g.label}
                        </Typography>
                        <Box component="ul" sx={{ columnWidth: 180 }}>
                            {g.cards.map((c) => (
                                <li key={c.id}>
                                    <Link
                                        component={RouterLink}
                                        to={cardPath(c)}
                                        data-slug={cardSlug(c)}
                                    >
                                        {nameOf(c.id)}
                                    </Link>
                                </li>
                            ))}
                        </Box>
                    </Box>
                ))}
                <CallToAction {...ui.cta} to="/ritual/daily" />
            </Article>
        </PageShell>
    );
};
